import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ImageIcon, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import Math from "@/components/Math";

function parseExplanation(text) {
  if (!text) return null;
  const out = { concepts: "", steps: [], answer: "", check: "", practice: "" };
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const l = raw.trim();
    if (!l) continue;
    if (/^CONCEPTS?:/i.test(l)) out.concepts = l.replace(/^CONCEPTS?:/i, "").trim();
    else if (/^STEP\s*\d+:/i.test(l)) out.steps.push(l.replace(/^STEP\s*\d+:/i, "").trim());
    else if (/^ANSWER:/i.test(l)) out.answer = l.replace(/^ANSWER:/i, "").trim();
    else if (/^CHECK:/i.test(l)) out.check = l.replace(/^CHECK:/i, "").trim();
    else if (/^PRACTICE:/i.test(l)) out.practice = l.replace(/^PRACTICE:/i, "").trim();
    else if (out.steps.length && !out.answer) out.steps[out.steps.length - 1] += " " + l;
  }
  if (!out.steps.length && !out.answer) out.answer = text;
  return out;
}

export default function Tutor() {
  const [question, setQuestion] = useState("");
  const [classLevel, setClassLevel] = useState(10);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [diagram, setDiagram] = useState(null);
  const [diaBusy, setDiaBusy] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const solve = async () => {
    if (!user) { nav("/auth?mode=login"); return; }
    if (!question.trim()) return;
    setBusy(true); setResult(null); setDiagram(null);
    try {
      const { data } = await api.post("/ai/explain", { question, class_level: classLevel });
      setResult(parseExplanation(data.answer));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI is busy — try again in a moment.");
    } finally { setBusy(false); }
  };

  const generateDiagram = async () => {
    if (!user) { nav("/auth?mode=login"); return; }
    if (!question.trim()) { toast.error("Type the problem first"); return; }
    setDiaBusy(true); setDiagram(null);
    try {
      const { data } = await api.post("/ai/diagram", { description: question });
      setDiagram(`data:${data.mime_type};base64,${data.image_base64}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Diagram generation failed");
    } finally { setDiaBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-1">Gyaan · AI Tutor</div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Ask any maths doubt.</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-xl">Paste your problem — get a numbered, teacher-style walkthrough with a final check. Add a diagram for geometry problems.</p>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#152040] p-6 mb-6">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mr-1">Class:</div>
          {[6, 7, 8, 9, 10].map((n) => (
            <button
              key={n}
              data-testid={`tutor-class-${n}`}
              onClick={() => setClassLevel(n)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                classLevel === n ? "bg-sky-400 text-slate-900 border-sky-400 font-semibold" : "border-white/10 text-zinc-400 hover:border-orange-300/40 hover:text-orange-300"
              }`}
            >
              Class {n}
            </button>
          ))}
        </div>
        <Textarea
          data-testid="tutor-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={4}
          placeholder="e.g. Solve 2x² − 7x + 3 = 0 using the quadratic formula"
          className="bg-[#0b1428] border-white/10 focus:border-cyan-400 font-mono"
        />
        <div className="flex flex-wrap gap-3 mt-4">
          <Button data-testid="tutor-solve-btn" onClick={solve} disabled={busy} className="bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Solving…" : "Explain step by step"}
          </Button>
          <Button data-testid="tutor-diagram-btn" onClick={generateDiagram} disabled={diaBusy} variant="outline" className="rounded-full border-white/15 bg-transparent">
            <ImageIcon className="w-4 h-4 mr-2" /> {diaBusy ? "Drawing…" : "Generate diagram"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-lg border border-white/10 bg-[#152040] p-6 fade-up" data-testid="tutor-result">
          {result.concepts && (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-1">Concepts</div>
              <div className="text-sm text-zinc-300"><Math>{result.concepts}</Math></div>
            </div>
          )}
          {result.steps.length > 0 && (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-2">Solution</div>
              <div className="rounded-md border border-white/10 bg-[#0b1428] p-4 relative overflow-hidden">
                <div className="bp-grid absolute inset-0 opacity-70" />
                <div className="relative space-y-2 font-mono text-sm">
                  {result.steps.map((s, i) => (
                    <div key={i} className="flex gap-3 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                      <span className="text-yellow-300">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-zinc-200 bp-math"><Math>{s}</Math></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {result.answer && (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-1">Answer</div>
              <div className="rounded-md bg-orange-300/10 border border-yellow-400/30 px-4 py-3 font-mono text-yellow-300">
                <Math>{result.answer}</Math>
              </div>
            </div>
          )}
          {result.check && (
            <div className="text-xs text-zinc-500 font-mono mb-2">✓ <Math>{result.check}</Math></div>
          )}
          {result.practice && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Try this next</div>
              <div className="text-sm text-zinc-300"><Math>{result.practice}</Math></div>
            </div>
          )}
        </div>
      )}

      {diagram && (
        <div className="mt-6 rounded-lg border border-white/10 bg-[#152040] p-6 fade-up" data-testid="tutor-diagram">
          <div className="text-[11px] uppercase tracking-wider text-pink-300 mb-3">AI-generated diagram</div>
          <img src={diagram} alt="AI diagram" className="w-full rounded-md border border-white/10" />
        </div>
      )}
    </div>
  );
}
